// generateVideoObjectJsonLd.js
import {
  getPostVideoObject,
  getPostPreviewVideoObject,
  getPostImageObject,
  getPostPreviewImageObject,
  getShortSchemaObject,
  getShortPreviewSchemaObject,
  getPerson,
  getInteractionCounter,
} from '../resource/SchemaObject.js';
import {
  getAssetTrailer,
  MediaAssetFormat,
  MediaPreset,
} from '../resource/getMediaAsset.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { DEFAULT_LANGUAGE } from '../resource/i18nCommon.js';
import { normalizeLang, getHreflang } from '../resource/langPath.js';

/**
 * Build name/value object with language tag
 * @param {string} lang - Language code
 * @param {Function} translateFn - Translation function that takes language code
 * @returns {string} Translated title or description
 */
const buildNameValueByLang = (lang, translateFn) => translateFn(lang);

/**
 * Generate alternate language objects for multilingual support
 * @param {Object} params
 * @param {string} params.inLanguage - Language code
 * @param {string} params.currentLanguage - Current language code
 * @param {string} params.i18nTitleKey - i18n translation key for title
 * @param {string} params.i18nDescriptionKey - i18n translation key for description
 * @param {string} params.title - Default title
 * @param {string} params.description - Default description
 * @param {Function} params.t - Translation function
 * @returns {Object} Object containing inLanguage, name, alternateName, description, alternateDescription
 */
const getAlternateObject = ({
  inLanguage,
  currentLanguage,
  i18nTitleKey,
  i18nDescriptionKey,
  title,
  description,
  t,
}) => {
  // Helper function to get translated title by language
  const getTitleByLang = lang =>
    t(i18nTitleKey, {
      title,
      lng: lang,
      ns: TranslationNamespace.SEO,
    });

  // Helper function to get translated description by language
  const getDescriptionByLang = lang =>
    t(i18nDescriptionKey, {
      caption: description || title,
      lng: lang,
      ns: TranslationNamespace.SEO,
    });

  return {
    inLanguage,
    name: buildNameValueByLang(currentLanguage, getTitleByLang),
    description: buildNameValueByLang(currentLanguage, getDescriptionByLang),
  };
};

/**
 * Generate VideoObject JSON-LD for video detail pages with multilingual support
 * @param {Object} params
 * @param {string} params.id - Message ID
 * @param {string} params.title - Video title
 * @param {string} params.description - Video description
 * @param {number} params.durationSecs - Video duration in seconds
 * @param {string} params.senderId - Sender user ID
 * @param {string} params.username - Author username
 * @param {number} params.postedAtUnix - Posted timestamp
 * @param {boolean} params.isFree - Whether video is free
 * @param {number} params.unlockPrice - Unlock price if not free
 * @param {Array} params.interactionStatistic - Array of interaction statistics {type, count}
 * @param {string} params.thumbnailUrl - Thumbnail URL
 * @param {Array<string>} params.hashtags - Array of hashtags
 * @param {Array<string>} params.categories - Array of categories
 * @param {string} params.origin - URL origin (default: 'https://swag.live')
 * @param {string} params.sourceLanguage - Source language code
 * @param {Array<string>} params.assetIds - Array of asset IDs
 * @param {Array<string>} params.relatedMessageIds - Array of related message IDs
 * @param {Array<string>} params.supportedLanguages - Array of supported language codes (optional, will use all available languages if not provided)
 * @param {Object} params.i18nKeys - Object containing i18n keys {title, description, previewTitle, previewDescription}
 * @param {string} params.offerDescription - Description for the offer (optional)
 * @param {Object} params.i18n - i18n instance (required)
 * @param {string} params.mediaType - Media type string (e.g., 'video/mp4', 'image/jpeg')
 * @param {string} params.contentType - Content type: 'post' or 'shorts' (default: 'post')
 * @returns {Promise<Object>} JSON-LD object with @graph structure
 */
export const generateVideoObjectJsonLd = async ({
  id,
  title,
  description,
  durationSecs,
  senderId,
  username,
  postedAtUnix,
  isFree,
  unlockPrice,
  interactionStatistic = [],
  thumbnailUrl,
  hashtags = [],
  categories = [],
  origin = 'https://swag.live',
  sourceLanguage,
  assetIds = [],
  relatedMessageIds = [],
  i18nKeys = {
    title: 'seo_video_detail_title',
    description: 'seo_video_detail_description',
    previewTitle: 'seo_video_detail_preview_title',
    previewDescription: 'seo_video_detail_preview_description',
  },
  offerDescription,
  i18n,
  mediaType,
  contentType = 'post',
}) => {
  const t = i18n.t;
  const currentLanguage = i18n.language;
  const langPath =
    normalizeLang(currentLanguage) === DEFAULT_LANGUAGE
      ? ''
      : `/${normalizeLang(currentLanguage)}`;
  const originWithLang = `${origin}${langPath}`;

  const normalizedInLanguage = getHreflang(currentLanguage);

  const loadLanguagePromise = async () => {
    if (!i18n.hasResourceBundle(currentLanguage, TranslationNamespace.SEO)) {
      await i18n.reloadResources(currentLanguage, TranslationNamespace.SEO);
    }
  };
  await loadLanguagePromise();

  // Convert interaction statistics to InteractionCounter format
  const formattedInteractionStatistic = interactionStatistic.map(
    getInteractionCounter
  );

  // Generate alternate objects for multilingual support
  const previewAlternateObject = getAlternateObject({
    inLanguage: normalizedInLanguage,
    currentLanguage,
    i18nTitleKey: i18nKeys.previewTitle,
    i18nDescriptionKey: i18nKeys.previewDescription,
    title,
    description,
    t,
  });

  const authorObject = {
    '@type': 'Person',
    '@id': `${originWithLang}/user/${senderId}#person`,
    name: username,
    url: `${originWithLang}/user/${senderId}`,
  };

  const fullAlternateObject = getAlternateObject({
    inLanguage: normalizedInLanguage,
    currentLanguage,
    i18nTitleKey: i18nKeys.title,
    i18nDescriptionKey: i18nKeys.description,
    title,
    description,
    t,
  });

  const isShorts = contentType === 'shorts';
  const isImage = mediaType?.startsWith('image');
  const contentBasePath = `${originWithLang}/${contentType}/${id}`;

  // Generate preview object with multilingual support
  const getPreviewObject = () => {
    if (isImage) {
      return getPostPreviewImageObject({
        id,
        author: authorObject,
        postedAtUnix,
        origin,
        langPath,
      });
    }
    if (isShorts) {
      return getShortPreviewSchemaObject({
        id,
        author: authorObject,
        postedAtUnix,
        origin,
        langPath,
        inLanguage: normalizedInLanguage,
      });
    }
    return getPostPreviewVideoObject({
      id,
      assetId: assetIds[0],
      title,
      description,
      postedAtUnix,
      sourceLanguage,
      author: authorObject,
      thumbnailUrl,
      origin,
      langPath,
    });
  };

  const previewVideoObject = {
    ...getPreviewObject(),
    ...previewAlternateObject,
  };

  const commonParams = {
    id,
    username,
    durationSecs,
    postedAtUnix,
    isFree,
    unlockPrice,
    author: authorObject,
    interactionStatistic: formattedInteractionStatistic,
    thumbnailUrl,
    hashtags,
    categories,
    origin,
    langPath,
  };

  // Generate full object with multilingual support
  const getFullObject = () => {
    if (isImage) {
      return getPostImageObject({
        id,
        author: authorObject,
        postedAtUnix,
        isFree,
        unlockPrice,
        interactionStatistic: formattedInteractionStatistic,
        origin,
        langPath,
        offerDescription,
      });
    }
    if (isShorts) {
      return getShortSchemaObject({
        ...commonParams,
        contentUrl: assetIds[0]
          ? getAssetTrailer({
              id,
              assetId: assetIds[0],
              format: MediaAssetFormat.MP4,
              preset: MediaPreset.SD,
              duration: 10,
            })
          : undefined,
        encodingFormat: assetIds[0] ? 'video/mp4' : undefined,
      });
    }
    return getPostVideoObject({
      ...commonParams,
      assetId: assetIds[0],
      title,
      description,
      sourceLanguage,
    });
  };

  const fullVideoObject = {
    ...getFullObject(),
    ...fullAlternateObject,
    // Override offers description if provided
    ...(offerDescription && !isFree
      ? {
          offers: {
            '@type': 'Offer',
            price: unlockPrice,
            priceCurrency: 'XXX',
            availability: 'https://schema.org/InStock',
            category: 'Pay-per-view',
            description: offerDescription,
          },
        }
      : {}),
    // Add about field for categories
    ...(categories.length > 0
      ? {
          about: categories.map(category => ({
            '@type': 'Thing',
            name: category,
            url: `${originWithLang}/category/${encodeURIComponent(category)}/${isShorts ? 'short' : 'flix'}`,
          })),
        }
      : {}),
  };

  // Helper function to get home title by language
  const getHomeTitleByLang = lang =>
    t('seo-home-title', {
      lng: lang,
      ns: TranslationNamespace.SEO,
    });

  // Generate breadcrumb list
  const breadcrumbList = {
    '@type': 'BreadcrumbList',
    '@id': `${contentBasePath}#breadcrumb`,
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: buildNameValueByLang(currentLanguage, getHomeTitleByLang),
        item: {
          '@id': `${originWithLang}/#website`,
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: fullVideoObject.name,
        item: { '@id': `${contentBasePath}#webpage` },
      },
    ],
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${contentBasePath}#webpage`,
        url: contentBasePath,
        breadcrumb: { '@id': `${contentBasePath}#breadcrumb` },
        mainEntity: { '@id': `${contentBasePath}#full` },
        inLanguage: normalizedInLanguage,
        name: fullVideoObject.name,
        description: fullVideoObject.description,
        ...(relatedMessageIds.length > 0
          ? {
              relatedLink: relatedMessageIds.map(
                messageId => `${originWithLang}/${contentType}/${messageId}`
              ),
            }
          : {}),
      },
      previewVideoObject,
      fullVideoObject,
      getPerson({ id: senderId, username, origin, langPath }),
      breadcrumbList,
    ],
  };
};

export default generateVideoObjectJsonLd;
